import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useIsMobile } from './hooks/useIsMobile';
import { useContent } from './hooks/useContent';
import Sidebar from './components/Sidebar';
import AskAgent from './components/AskAgent';
import HireTimer from './components/HireTimer';
import ContactModal from './components/ContactModal';
import Overview from './pages/Overview';
import Portfolio from './pages/Portfolio';
import Resume from './pages/Resume';
import { About, Metrics, Skills, Systems, Awards, Timeline } from './pages/Pages';
import Admin from './admin/Admin';
import './index.css';

// All page components accept a `content` prop — no wrapper needed.
// `resume` is intentionally absent from the Sidebar nav — it's a link-only page (/#resume).
const PAGES = {
  overview: Overview,
  about: About,
  metrics: Metrics,
  skills: Skills,
  portfolio: Portfolio,
  systems: Systems,
  awards: Awards,
  timeline: Timeline,
  resume: Resume,
};

function MainSite({ content }) {
  const isMobile = useIsMobile();
  const [currentPage, setCurrentPage] = useState(() => {
    const hash = window.location.hash.slice(1);
    return PAGES[hash] ? hash : 'overview';
  });
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 768);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    const open = () => setContactOpen(true);
    window.addEventListener('mr:openContact', open);
    return () => window.removeEventListener('mr:openContact', open);
  }, []);

  // Keep URL hash in sync so refresh restores the current page
  useEffect(() => {
    window.location.hash = currentPage === 'overview' ? '' : currentPage;
  }, [currentPage]);

  // Handle browser back/forward
  useEffect(() => {
    const onHash = () => {
      const hash = window.location.hash.slice(1);
      const page = PAGES[hash] ? hash : 'overview';
      setCurrentPage(page);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const PageComponent = PAGES[currentPage] || Overview;

  const handleNavigate = (page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
    if (isMobile) setSidebarOpen(false);
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <HireTimer />

      {/* Mobile backdrop — tapping it closes the sidebar */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 199 }}
        />
      )}

      {/* Floating contact button */}
      <button
        className="no-print"
        onClick={() => window.dispatchEvent(new CustomEvent('mr:openContact'))}
        style={{
          position: 'fixed',
          bottom: 16,
          ...(isMobile
            ? { left: '50%', transform: 'translateX(-50%)', right: 'auto' }
            : { left: sidebarOpen ? 266 : 16, transform: 'none' }),
          zIndex: 250,
          background: 'linear-gradient(135deg, var(--crimson), var(--deep-rose))',
          color: 'white', borderRadius: 14,
          padding: '10px 18px', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 7,
          fontFamily: "'Nunito', sans-serif", fontSize: 14, fontWeight: 800,
          boxShadow: 'var(--shadow), 0 0 0 3px var(--blush)',
          transition: isMobile ? 'none' : 'left .35s cubic-bezier(.4,0,.2,1)',
          whiteSpace: 'nowrap',
        }}
        onMouseEnter={e => { e.currentTarget.style.boxShadow = 'var(--shadow-hover), 0 0 0 3px var(--blush)'; }}
        onMouseLeave={e => { e.currentTarget.style.boxShadow = 'var(--shadow), 0 0 0 3px var(--blush)'; }}
      >
        ✉ Contact Makayla
      </button>

      {/* Decorative stars — hide on mobile */}
      {!isMobile && <>
        <span className="star" style={{ top: '7%', right: '4%', fontSize: 14, animationDelay: '0s' }}>✦</span>
        <span className="star" style={{ top: '21%', right: '11%', fontSize: 9, animationDelay: '.8s' }}>✦</span>
        <span className="star" style={{ top: '52%', right: '3%', fontSize: 12, animationDelay: '1.5s' }}>✦</span>
        <span className="star" style={{ top: '76%', right: '7%', fontSize: 10, animationDelay: '.3s' }}>✦</span>
      </>}

      <Sidebar
        content={content}
        currentPage={currentPage}
        onNavigate={handleNavigate}
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(o => !o)}
        isMobile={isMobile}
      />

      <main style={{
        marginLeft: isMobile ? 0 : (sidebarOpen ? 250 : 0),
        flex: 1,
        padding: isMobile ? '16px 16px 80px' : '28px 32px',
        minHeight: '100vh',
        position: 'relative',
        zIndex: 1,
        transition: 'margin-left .35s cubic-bezier(.4,0,.2,1)',
      }}>
        <AskAgent agentLabel={content?.profile?.agentLabel} />
        <PageComponent content={content} />
      </main>
      {contactOpen && <ContactModal onClose={() => setContactOpen(false)} />}
    </div>
  );
}

export default function App() {
  const { content, loading, error, saveContent, verifyPassword } = useContent();

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--cream)' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontStyle: 'italic', color: 'var(--deep-rose)', marginBottom: 8 }}>Loading...</div>
        <div style={{ fontFamily: "'Courier Prime', monospace", fontSize: 12, color: 'var(--text3)' }}>makayla-portfolio</div>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--cream)' }}>
      <div style={{ textAlign: 'center', color: 'var(--crimson)' }}>Error loading content: {error}</div>
    </div>
  );

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<MainSite content={content} />} />
        <Route path="/admin" element={<Admin content={content} onSave={saveContent} onVerify={verifyPassword} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
